import { API_URL } from '@/config/constants'
import axios from 'axios'
import { useUserStore } from '@/store/userStore'

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

// Request interceptor
// HTTP-only cookies are sent automatically with withCredentials: true
// No need to manually attach tokens
api.interceptors.request.use(
  (config) => {
    // Log timetable check-limit requests
    if (config.url?.includes('/timetable/check-limit')) {
  // console.log🌐 [AXIOS REQUEST] GET /timetable/check-limit')
  // console.log🌐 [AXIOS REQUEST] Full URL:', config.baseURL + config.url)
  // console.log🌐 [AXIOS REQUEST] Method:', config.method)
  // console.log🌐 [AXIOS REQUEST] Headers:', config.headers)
    }
    // Cookies are sent automatically by browser
    return config
  },
  (error) => {
    // console.error('Request interceptor error:', error)
    return Promise.reject(error)
  }
)

// Helper to recursively fix HTTP/HTTPS URLs
const fixHttpsUrls = (obj: any): any => {
  if (!obj) return obj

  if (typeof obj === 'string') {
    // Special case 1: CDN URLs should use HTTPS
    if (obj.startsWith('http://46.101.199.181')) {
  // console.logConverting HTTP to HTTPS for CDN URL:', obj)
      return obj.replace('http://', 'https://')
    }

    // Special case 2: Dicebear URLs should use HTTPS
    if (obj.includes('api.dicebear.com') && obj.startsWith('http://')) {
  // console.logConverting HTTP to HTTPS for Dicebear URL:', obj)
      return obj.replace('http://', 'https://')
    }
  }

  if (typeof obj === 'object') {
    // Process arrays
    if (Array.isArray(obj)) {
      return obj.map((item) => fixHttpsUrls(item))
    }

    // Process objects
    const newObj = { ...obj }
    for (const key in newObj) {
      newObj[key] = fixHttpsUrls(newObj[key])
    }
    return newObj
  }

  return obj
}

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Log timetable check-limit responses
    if (response.config.url?.includes('/timetable/check-limit')) {
  // console.log🌐 [AXIOS RESPONSE] GET /timetable/check-limit')
  // console.log🌐 [AXIOS RESPONSE] Status:', response.status)
  // console.log🌐 [AXIOS RESPONSE] Data:', response.data)
  // console.log🌐 [AXIOS RESPONSE] Full Response:', response)
    } else {
  // console.logResponse successful:', response.status)
    }

    // Fix any HTTPS CDN URLs in the response data
    if (response.data) {
      response.data = fixHttpsUrls(response.data)
    }

    return response
  },
  (error) => {
    // console.error('Response error:', error.response?.status, error.message)
    // console.error('Error response data:', error.response?.data)
    // console.error('Error request URL:', error.config?.url)

    if (error.response?.status === 401) {
      // Don't auto-logout for profile update endpoints - let the form handle the error
      const profileUpdateEndpoints = [
        '/user-controller/updateprofile',
        '/user-controller/updateme'
      ]
      
      // Don't auto-logout for login/auth endpoints - let them handle errors
      const authEndpoints = [
        '/auth/login',
        '/auth/register',
        '/auth/logout',
        '/auth/forgot-password',
        '/auth/reset-password'
      ]
      
      // Don't auto-logout for session verification endpoints - they're used to check session status
      const sessionVerificationEndpoints = [
        '/user-controller/session'
      ]
      
      // Don't auto-logout for exam timetable endpoints - let the component handle the error
      const examTimetableEndpoints = [
        '/exam-time-slots',
        '/exam-time-slots/exam',
        '/exam-time-slots/exam-day',
        '/exam-time-slots/schedule'
      ]
      
      const isProfileUpdate = profileUpdateEndpoints.some(endpoint => 
        error.config?.url?.includes(endpoint)
      )
      
      const isAuthEndpoint = authEndpoints.some(endpoint => 
        error.config?.url?.includes(endpoint)
      )
      
      const isSessionVerification = sessionVerificationEndpoints.some(endpoint => 
        error.config?.url?.includes(endpoint)
      )
      
      const isExamTimetable = examTimetableEndpoints.some(endpoint => 
        error.config?.url?.includes(endpoint)
      )

      if (isProfileUpdate) {
    // console.log401 error on profile update - not auto-logging out, letting form handle error')
        // Just return the error, don't logout
        return Promise.reject(error)
      }
      
      if (isAuthEndpoint) {
    // console.log401 error on auth endpoint - not auto-logging out, letting auth handle error')
        // Just return the error, don't logout
        return Promise.reject(error)
      }
      
      if (isSessionVerification) {
    // console.log401 error on session verification endpoint - not auto-logging out, session check failed')
        // Just return the error, don't logout - this is used to check session status
        return Promise.reject(error)
      }
      
      if (isExamTimetable) {
    // console.log401 error on exam timetable endpoint - not auto-logging out, letting component handle error')
        // Just return the error, don't logout - let the component show a user-friendly message
        return Promise.reject(error)
      }
      
      // Check if user exists in store before logging out
      // If user exists, they just logged in - don't logout (cookie might not be ready yet)
      // This respects HTTP-only cookies - if user just logged in, preserve their session
      const user = useUserStore.getState().user
      if (user) {
    // console.log401 error but user exists in store (just logged in) - not logging out, cookie may not be ready yet')
        // Just return the error, don't logout
        return Promise.reject(error)
      }

      // Session expired (HTTP-only cookie invalid/expired) - logout and redirect
      // Only logout if no user exists in store (session actually expired)
  // console.logSession expired - logging out and redirecting to login')
      useUserStore.getState().logout().finally(() => {
        // Always redirect to login after logout
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.replace('/login')
        }
      })
    } else if (error.response?.status === 403) {
  // console.logForbidden error detected, user lacks permission')
      // HTTP-only cookie exists but user lacks permission
      // You could show a toast notification here
    }

    return Promise.reject(error)
  }
)

export default api
